import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workoutService } from '../services/workoutService';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { useCurrentWorkout } from '../contexts/WorkoutContext';
import { LazyImage, LazyVideo } from '../components/LazyMedia';
import './WorkoutDetail.css';

const WorkoutDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedExercises, setExpandedExercises] = useState(new Set());
  const [workoutProgress, setWorkoutProgress] = useState(null);
  const [completedExercises, setCompletedExercises] = useState(new Set());
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showActiveWorkoutModal, setShowActiveWorkoutModal] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: '', title: '' });
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const videoRef = React.useRef(null);
  const { workoutProgress: globalWorkoutProgress, refreshCurrentWorkout } = useCurrentWorkout();
  const activeWorkoutProgress = useMemo(
    () => (globalWorkoutProgress && !globalWorkoutProgress.completed ? globalWorkoutProgress : null),
    [globalWorkoutProgress]
  );

  useEffect(() => {
    loadWorkout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
  }, []);

  const loadWorkout = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workoutService.getWorkoutById(id);
      setWorkout(data);

      // Expand first exercise by default
      if (data.exercises && data.exercises.length > 0) {
        setExpandedExercises(new Set([data.exercises[0].exerciseId]));
      }
    } catch (err) {
      console.error('Error loading workout:', err);
      setError('Не удалось загрузить тренировку');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeWorkoutProgress && activeWorkoutProgress.workoutId === parseInt(id, 10)) {
      setWorkoutProgress(activeWorkoutProgress);
      const completed = new Set();
      activeWorkoutProgress.exercisesProgress?.forEach((ep) => {
        if (ep.completed) {
          completed.add(ep.exerciseId);
        }
      });
      setCompletedExercises(completed);
    } else {
      setWorkoutProgress(null);
      setCompletedExercises(new Set());
    }
  }, [activeWorkoutProgress, id]);

  const handleStartWorkout = async () => {
    // Check if there's an active workout for a different workout
    if (activeWorkoutProgress && activeWorkoutProgress.workoutId !== parseInt(id)) {
      setShowActiveWorkoutModal(true);
      return;
    }

    try {
      setIsStarting(true);
      const progress = await workoutService.startWorkout(parseInt(id));
      setWorkoutProgress(progress);
      alert('Тренировка начата! Отмечайте упражнения по мере выполнения.');
      await refreshCurrentWorkout();
    } catch (err) {
      console.error('Error starting workout:', err);
      const errorMsg = err.response?.data?.message || err.message;
      alert('Ошибка: ' + errorMsg);
    } finally {
      setIsStarting(false);
    }
  };

  const handleGoToActiveWorkout = () => {
    if (activeWorkoutProgress && activeWorkoutProgress.workout) {
      navigate(`/app/workouts/${activeWorkoutProgress.workout.id}`);
    }
  };

  const openVideoModal = (videoUrl, exerciseName) => {
    if (!videoUrl) {
      return;
    }
    const resolvedUrl = resolveMediaUrl(videoUrl);
    console.log('Opening video modal:', { videoUrl, resolvedUrl, exerciseName });
    setVideoModalOpen(true);
    setVideoError(null);
    setVideoLoading(true);
    setCurrentVideo({ url: resolvedUrl, title: exerciseName });

    // iOS: Force video load after a short delay
    if (isIOS) {
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, 300);
    }
  };

  const closeVideoModal = () => {
    // Pause and reset video before closing
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setVideoModalOpen(false);
    setCurrentVideo({ url: '', title: '' });
    setVideoError(null);
    setVideoLoading(false);
  };

  const handleCompleteExercise = async (exercise) => {
    if (!workoutProgress) {
      alert('Сначала начните тренировку');
      return;
    }

    try {
      await workoutService.completeExercise(workoutProgress.id, {
        exerciseId: exercise.exerciseId
      });
      await refreshCurrentWorkout();

      setCompletedExercises(prev => new Set([...prev, exercise.exerciseId]));
      alert(`✓ Упражнение "${exercise.name}" завершено!`);
    } catch (err) {
      console.error('Error completing exercise:', err);
      alert('Ошибка при завершении упражнения: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCompleteWorkout = async () => {
    if (!workoutProgress) {
      alert('Тренировка не начата');
      return;
    }

    const confirmComplete = window.confirm(
      'Завершить тренировку? Все незавершенные упражнения будут отмечены как выполненные.'
    );

    if (!confirmComplete) {
      return;
    }

    try {
      setIsCompleting(true);
      await workoutService.completeWorkout(workoutProgress.id);
      alert('🎉 Тренировка завершена! Отличная работа!');
      await refreshCurrentWorkout();
      navigate('/app');
    } catch (err) {
      console.error('Error completing workout:', err);
      alert('Ошибка: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsCompleting(false);
    }
  };

  const completedCount = completedExercises.size;
  const isAlreadyCompleted = useMemo(() => {
    if (!workout) {
      return false;
    }
    if (activeWorkoutProgress) {
      return false;
    }
    return Boolean(workout.completed || workoutProgress?.completed);
  }, [activeWorkoutProgress, workout, workoutProgress]);

  if (loading) {
    return (
      <div className="workout-detail">
        <div className="loading-state">Загрузка тренировки...</div>
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="workout-detail">
        <div className="error-state">
          <p>{error || 'Тренировка не найдена'}</p>
          <button onClick={() => navigate('/app')} className="btn-primary">
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  const toggleExercise = (exerciseId) => {
    setExpandedExercises(prev => {
      const newSet = new Set(prev);
      if (newSet.has(exerciseId)) {
        newSet.delete(exerciseId);
      } else {
        newSet.add(exerciseId);
      }
      return newSet;
    });
  };

  return (
    <div className="workout-detail">
      <div className="workout-header">
        <button onClick={() => navigate('/app')} className="back-button">
          ← Назад
        </button>
        <div className="workout-title-section">
          <h1>{workout.name}</h1>
          <p className="workout-description">{workout.description}</p>
        </div>
        <div className="workout-meta-info">
          <div className="meta-badge">
            <span className="meta-icon">📅</span>
            <span>Месяц {workout.monthNumber}</span>
          </div>
          <div className="meta-badge">
            <span className="meta-icon">📋</span>
            <span>{completedCount} / {workout.exerciseCount} упражнений</span>
          </div>
        </div>
      </div>

      {isAlreadyCompleted && (
        <div className="workout-completed-banner">
          🎉 Эта тренировка уже завершена. Вы можете пересмотреть упражнения или выбрать новую.
        </div>
      )}

      <div className="workout-actions">
        {!workoutProgress && (
          <button
            onClick={handleStartWorkout}
            className="btn-primary btn-large"
            disabled={isStarting}
          >
            {isStarting ? 'Начинаем...' : '🏋️ Начать тренировку'}
          </button>
        )}

        {workoutProgress && (
          <>
            <button
              onClick={() => navigate(`/app/workouts/${workout.id}`)}
              className="btn-primary btn-large"
              style={{ marginBottom: '1rem' }}
            >
              ▶ Продолжить тренировку
            </button>
            <div className="progress-indicator">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${(completedCount / workout.exerciseCount) * 100}%` }}
                />
              </div>
              <p>Завершено {completedCount} из {workout.exerciseCount} упражнений</p>
            </div>
            <button
              onClick={handleCompleteWorkout}
              className="btn-success btn-large"
              disabled={isCompleting}
              style={{ marginTop: '1rem' }}
            >
              {isCompleting ? 'Завершаем...' : '✓ Завершить тренировку'}
            </button>
          </>
        )}
      </div>

      <div className="exercises-section">
        <div className="exercises-header">
          <h2>Упражнения</h2>
          <p className="exercises-hint">👆 Нажмите на упражнение чтобы посмотреть детали</p>
        </div>
        <div className="exercises-list">
          {workout.exercises && workout.exercises.map((exercise, index) => {
            const isCompleted = completedExercises.has(exercise.exerciseId);
            const isExpanded = expandedExercises.has(exercise.exerciseId);

            return (
              <div
                key={exercise.workoutExerciseId}
                className={`exercise-card ${isExpanded ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="exercise-header" onClick={() => toggleExercise(exercise.exerciseId)}>
                  <div className={`exercise-number ${isCompleted ? 'completed' : ''}`}>
                    {isCompleted ? '✓' : exercise.order}
                  </div>
                  <div className="exercise-title">
                    <h3>{exercise.name}</h3>
                    {exercise.sets && exercise.repetitions && (
                      <div className="exercise-sets-info">
                        <span className="badge">{exercise.sets} подходов</span>
                        <span className="badge">{exercise.repetitions} повторений</span>
                        {exercise.restSeconds && (
                          <span className="badge">Отдых: {exercise.restSeconds}с</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="exercise-actions">
                    {workoutProgress && !isCompleted && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteExercise(exercise);
                        }}
                        className="btn-complete-exercise"
                      >
                        ✓ Завершить
                      </button>
                    )}
                    <button className="expand-icon">
                      {isExpanded ? '▼' : '▶'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="exercise-details">
                    {exercise.previewUrl && (
                      <div className="exercise-preview-wrapper">
                        <LazyImage
                          src={resolveMediaUrl(exercise.previewUrl)}
                          alt={exercise.name}
                          className="exercise-preview"
                          rootMargin={300}
                          onError={(e) => {
                            console.log('Failed to load preview:', exercise.name);
                          }}
                        />
                      </div>
                    )}

                    {exercise.description && (
                      <div className="exercise-description">
                        <h4>Техника выполнения:</h4>
                        <p>{exercise.description}</p>
                      </div>
                    )}

                    {exercise.videoUrl && (
                      <div className="video-section">
                        <button
                          onClick={() => openVideoModal(exercise.videoUrl, exercise.name)}
                          className="btn-video"
                        >
                          📹 Смотреть видео упражнения
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {workoutProgress && (
        <div className="workout-footer-actions">
          <button
            onClick={handleCompleteWorkout}
            className="btn-success btn-large"
            disabled={isCompleting}
          >
            {isCompleting ? 'Завершаем...' : '✓ Завершить тренировку'}
          </button>
        </div>
      )}

      {/* Modal for active workout warning */}
      {showActiveWorkoutModal && (
        <div className="modal-overlay" onClick={() => setShowActiveWorkoutModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Активная тренировка</h3>
            </div>
            <div className="modal-body">
              <p>У вас уже есть начатая тренировка:</p>
              <div className="active-workout-info">
                <strong>#{activeWorkoutProgress?.workout?.workoutNumber} {activeWorkoutProgress?.workout?.name}</strong>
              </div>
              <p>Завершите текущую тренировку перед началом новой.</p>
            </div>
            <div className="modal-footer">
              <button
                onClick={handleGoToActiveWorkout}
                className="btn-primary"
              >
                Перейти к активной тренировке
              </button>
              <button
                onClick={() => setShowActiveWorkoutModal(false)}
                className="btn-secondary"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoModalOpen && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div className="modal-content video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📹 {currentVideo.title}</h3>
              <button onClick={closeVideoModal} className="close-button">×</button>
            </div>
            <div className="modal-body video-modal-body">
              {videoLoading && (
                <div className="video-loading">Загрузка видео...</div>
              )}
              {videoError && (
                <div className="video-error">{videoError}</div>
              )}
              {currentVideo.url && (
                <video
                  ref={videoRef}
                  className="video-player"
                  title={currentVideo.title}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  playsInline
                  playsinline="true"
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  preload={isIOS ? 'auto' : 'metadata'}
                  crossOrigin="anonymous"
                  onContextMenu={(e) => e.preventDefault()}
                  onLoadStart={() => {
                    console.log('Video load started');
                    setVideoLoading(true);
                  }}
                  onLoadedMetadata={() => {
                    console.log('Video metadata loaded');
                    if (!isIOS) {
                      setVideoLoading(false);
                    }
                  }}
                  onLoadedData={() => {
                    console.log('Video data loaded');
                    setVideoLoading(false);
                  }}
                  onError={(e) => {
                    console.error('Video error:', e);
                    setVideoLoading(false);
                    setVideoError('Не удалось загрузить видео. Попробуйте позже.');
                  }}
                  onCanPlay={() => {
                    console.log('Video can play');
                    setVideoLoading(false);
                  }}
                  onWaiting={() => console.log('Video waiting')}
                  onStalled={() => {
                    console.log('Video stalled');
                    // iOS Safari: retry loading if stalled
                    if (isIOS && videoRef.current) {
                      setTimeout(() => {
                        if (videoRef.current && videoRef.current.readyState < 2) {
                          videoRef.current.load();
                        }
                      }, 1000);
                    }
                  }}
                  onSuspend={() => {
                    console.log('Video suspended');
                    // iOS Safari: try to resume if suspended
                    if (isIOS && videoRef.current && videoRef.current.readyState < 2) {
                      videoRef.current.load();
                    }
                  }}
                  style={{
                    display: videoLoading ? 'none' : 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                >
                  <source src={currentVideo.url} type="video/mp4" />
                  <source src={currentVideo.url} type="video/webm" />
                  Ваш браузер не поддерживает воспроизведение видео.
                </video>
              )}
              {!currentVideo.url && !videoError && (
                <div className="video-error">Видео недоступно.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutDetail;
